import React, { useEffect, useState } from "react"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import PageContent from "components/common/LayoutComponents/PageContent"
import Button from "@mui/material/Button"
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded"
import MediaManager from "components/common/MediaManager"
import { StyledMedia } from "./Media.style"
import { MEDIA_TYPE_INDEX, MEDIA_TYPES } from "constants/global"
import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import CardActions from "@mui/material/CardActions"
import Typography from "@mui/material/Typography"
import { useSnackbar } from "notistack"

import { getResourceStatInfo } from "services/media"
import { verifyResponse } from "utils/httpRequest"
import LoadingScreen from "components/common/LoadingScreen"
import { PERMISSIONS } from "constants/permission"
import { UsePermissionVerification } from "components/customHooks"

const bull = (
  <Box
    component="span"
    sx={{ display: "inline-block", mx: "2px", transform: "scale(0.8)" }}
  >
    •
  </Box>
)

const ALL_MEDIA_TYPES = [
  MEDIA_TYPE_INDEX.image,
  MEDIA_TYPE_INDEX.video,
  MEDIA_TYPE_INDEX.audio,
  MEDIA_TYPE_INDEX.document,
  MEDIA_TYPE_INDEX.unknown,
]

const getMediaDescription = (type) => {
  if (type === MEDIA_TYPE_INDEX.unknown) return "Unclassified resources."
  if (type === MEDIA_TYPE_INDEX.audio) return "Audio resources (mp3)."
  if (type === MEDIA_TYPE_INDEX.video) return "Video resources (mp4)."
  if (type === MEDIA_TYPE_INDEX.image) return "Image resources."
  if (type === MEDIA_TYPE_INDEX.document)
    return "Document resources (PDF, docx, xlsx)."
  return "Media resources."
}

const Media = () => {
  const [openMediaManager, mediaManager] = MediaManager()
  const [statInfo, setStatInfo] = useState([])
  const [loading, setLoading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const AccessControl = UsePermissionVerification()

  const loadResources = async () => {
    try {
      setLoading(true)
      const response = await getResourceStatInfo()
      verifyResponse(response)
      if (response.rtnCode === 200) {
        setStatInfo(response.data || [])
      } else {
        setStatInfo([])
      }
    } catch {
      enqueueSnackbar("Failed to load media statistics.", { variant: "warning" })
      setStatInfo([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadResources()
  }, [])

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      listMediaAccess: AccessControl.VerifyAccess([
        PERMISSIONS.RESOURCE.RESOURCE_LIST,
      ]),
      statMediaAccess: AccessControl.VerifyAccess([
        PERMISSIONS.RESOURCE.RESOURCE_STAT_INFO,
      ]),
      searchMediaAccess: AccessControl.VerifyAccess([
        PERMISSIONS.RESOURCE.RESOURCE_QUERY_SEARCH,
      ]),
      addMediaAccess: AccessControl.VerifyAccess([PERMISSIONS.RESOURCE.RESOURCE_ADD]),
    })
  }, [AccessControl.UserPermissions])

  const canViewMedia =
    AccessControl.Access?.listMediaAccess ||
    AccessControl.Access?.statMediaAccess ||
    AccessControl.Access?.searchMediaAccess

  const canOpenManager =
    AccessControl.Access?.searchMediaAccess || AccessControl.Access?.addMediaAccess

  return (
    <PageContent>
      <StyledMedia>
        <PageHeading>
          <div className="heading-copy">
            <h1 className="page-title">Media</h1>
            <p className="page-subtitle">
              Browse media inventory stats and open the media manager to search,
              upload, and maintain assets.
            </p>
          </div>
          <div className="actions">
            {canOpenManager && (
              <Button
                className="open-media-btn"
                variant="contained"
                size="medium"
                startIcon={<AddCircleOutlineRoundedIcon />}
                onClick={() => {
                  openMediaManager(null, ALL_MEDIA_TYPES)
                }}
              >
                Open Media Manager
              </Button>
            )}
          </div>
        </PageHeading>
        <div className="page-surface">
          {canViewMedia && (
            <div className="item-list">
              {statInfo.map((mediaResourceTypeInfo) => {
                const mediaType =
                  MEDIA_TYPES.find(
                    (item) => item.value === mediaResourceTypeInfo.type
                  )?.text || "Unknown"
                return (
                  <Box
                    className="item"
                    key={`card-item-${mediaResourceTypeInfo.type}`}
                  >
                    <Card variant="outlined">
                      <CardContent>
                        <Typography sx={{ fontSize: 12 }} color="text.secondary">
                          Resource Type
                        </Typography>
                        <h3 className="resource-type">{mediaType}</h3>
                        <Typography className="count-line">
                          <span className="count-label">
                            {mediaResourceTypeInfo.typeCount}
                          </span>
                          {bull}
                          {mediaResourceTypeInfo.typeCount > 1
                            ? "Records"
                            : "Record"}
                        </Typography>
                        <Typography className="resource-desc" variant="body2">
                          {getMediaDescription(mediaResourceTypeInfo.type)}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        {canOpenManager && (
                          <Button
                            className="find-btn"
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              openMediaManager(
                                null,
                                [mediaResourceTypeInfo.type]
                              )
                            }}
                          >
                            Find Resource
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </Box>
                )
              })}
              {statInfo.length === 0 && (
                <div className="page-empty">No media statistics available.</div>
              )}
            </div>
          )}
          {!canViewMedia && (
            <div className="page-empty">
              You do not have permission to view media resources.
            </div>
          )}
        </div>
        {mediaManager}
        <LoadingScreen loading={loading} />
      </StyledMedia>
    </PageContent>
  )
}

export default Media
